import { useEffect, useState } from "react";
import { api, ApiError } from "../api.ts";
import { ErrorBanner } from "../ErrorBanner.tsx";
import { Pagination } from "../Pagination.tsx";
import { useCommunity } from "../community.ts";
import { useHash } from "../useHash.ts";
import type { Pagination as PageMeta, Report } from "../types.ts";

const PAGE_SIZE = 25;

function targetHref(r: Report): string {
  if (r.targetType === "listing") {
    return `#content/listing/${encodeURIComponent(r.targetId)}`;
  }
  if (r.targetType === "safety_note") {
    return `#content/safety-note/${encodeURIComponent(r.targetId)}`;
  }
  return "#";
}

export function Reports() {
  const hash = useHash();
  const resolved = hash.includes("resolved=true");
  const community = useCommunity();
  const [items, setItems] = useState<Report[]>([]);
  const [pagination, setPagination] = useState<PageMeta | null>(null);
  const [offset, setOffset] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  // Reset to first page when scope/filters change.
  useEffect(() => {
    setOffset(0);
  }, [resolved, community]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    const params = new URLSearchParams();
    params.set("resolved", resolved ? "true" : "false");
    params.set("limit", String(PAGE_SIZE));
    params.set("offset", String(offset));
    if (community) params.set("community_id", community);
    api<{ items: Report[]; pagination: PageMeta }>(
      `/admin/reports?${params.toString()}`,
    )
      .then((data) => {
        if (cancelled) return;
        setItems(data.items);
        setPagination(data.pagination);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e instanceof ApiError ? e.message : String(e));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [resolved, community, offset, refreshKey]);

  async function reopen(reportId: string) {
    setBusy(`reopen-${reportId}`);
    setError(null);
    try {
      await api(
        `/admin/reports/${encodeURIComponent(reportId)}/reopen`,
        { method: "POST" },
      );
      setRefreshKey((k) => k + 1);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  }

  return (
    <>
      <div className="view-head">
        <h2>Reports</h2>
        <span className="view-meta">
          User-submitted reports against listings and safety notes.
        </span>
      </div>
      <div className="filter-row">
        <div className="segment" role="tablist" aria-label="Report status">
          <a
            href="#reports"
            role="tab"
            aria-selected={!resolved}
            className={!resolved ? "is-active" : ""}
          >
            Open
            {pagination && !resolved && (
              <span className="count">{pagination.total}</span>
            )}
          </a>
          <a
            href="#reports?resolved=true"
            role="tab"
            aria-selected={resolved}
            className={resolved ? "is-active" : ""}
          >
            Resolved
            {pagination && resolved && (
              <span className="count">{pagination.total}</span>
            )}
          </a>
        </div>
      </div>

      {error && <ErrorBanner code={error} />}

      {loading && items.length === 0 ? (
        <div className="spinner-block">Loading reports…</div>
      ) : items.length === 0 ? (
        <div className="card">
          <div className="empty">
            <strong>No {resolved ? "resolved" : "open"} reports</strong>
            <span>
              {community
                ? `Nothing in community #${community} matches this filter.`
                : "Nothing matches this filter across any community."}
            </span>
          </div>
        </div>
      ) : (
        <div className="card">
          <table className="luna-table luna-table-hover">
            <thead>
              <tr>
                <th>Report</th>
                <th>Target</th>
                <th>Reason</th>
                <th>Reporter</th>
                <th>Created</th>
                <th>Status</th>
                {resolved && <th></th>}
              </tr>
            </thead>
            <tbody>
              {items.map((r) => {
                const reporterName = r.reporter?.displayName ?? null;
                const priorCount = r.reporter?.priorReportCount ?? 0;
                return (
                  <tr key={r.id}>
                    <td>#{r.id}</td>
                    <td>
                      {r.targetType}{" "}
                      <a href={targetHref(r)}>#{r.targetId}</a>
                    </td>
                    <td>
                      {r.reason}
                      {r.detail && <div className="detail">{r.detail}</div>}
                    </td>
                    <td className="meta">
                      {reporterName ? (
                        <>
                          {reporterName}{" "}
                          <span style={{ opacity: 0.7 }}>
                            (user #{r.reporterUserId})
                          </span>
                        </>
                      ) : (
                        <>user #{r.reporterUserId}</>
                      )}
                      <div className="detail" data-prior-report-count={priorCount}>
                        {priorCount === 0
                          ? "no prior reports"
                          : priorCount === 1
                            ? "1 prior report"
                            : `${priorCount} prior reports`}
                      </div>
                    </td>
                    <td className="meta">{r.createdAt}</td>
                    <td>
                      {r.resolved ? (
                        <span className="luna-badge luna-badge-success">
                          resolved
                        </span>
                      ) : (
                        <span className="luna-badge luna-badge-warning">
                          open
                        </span>
                      )}
                    </td>
                    {resolved && (
                      <td>
                        <button
                          type="button"
                          className="luna-btn luna-btn-outline luna-btn-sm"
                          onClick={() => void reopen(r.id)}
                          disabled={busy !== null}
                        >
                          {busy === `reopen-${r.id}` ? "Reopening…" : "Reopen"}
                        </button>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      <Pagination
        total={pagination?.total ?? 0}
        limit={PAGE_SIZE}
        offset={offset}
        onChange={setOffset}
        loading={loading}
        label="Reports"
      />
    </>
  );
}
